import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import { blogPosts, getPostBySlug } from '../data/blogPosts';
import Footer from '../components/Footer';

const CATEGORY_COLORS = {
  'Plugin Development': { color: 'var(--ms-blue)', bg: 'var(--ms-blue-pale)' },
  'Hiring Advice':      { color: 'var(--ms-teal)', bg: '#DFF6F0' },
  'Power Platform':     { color: 'var(--ms-purple)', bg: '#F4F0FF' },
  'PCF Development':    { color: 'var(--ms-orange)', bg: '#FFF1EC' },
  'CRM Customization':  { color: 'var(--ms-blue-dark)', bg: '#EFF6FC' },
  'PVA & Chatbots':     { color: 'var(--ms-teal)', bg: '#DFF6F0' },
};

function renderSection(section, i, onNavigate) {
  const textStyle = { fontSize: '16px', color: 'var(--text-m)', lineHeight: 1.8, marginBottom: '20px' };
  const h2Style  = { fontSize: 'clamp(18px,2vw,24px)', fontWeight: 700, color: 'var(--text-h)', marginTop: '44px', marginBottom: '14px', letterSpacing: '-0.01em' };
  const liStyle  = { fontSize: '16px', color: 'var(--text-m)', lineHeight: 1.8, marginBottom: '8px' };

  switch (section.type) {
    case 'p':
      return <p key={i} style={textStyle}>{section.content}</p>;

    case 'h2':
      return <h2 key={i} style={h2Style}>{section.content}</h2>;

    case 'h3':
      return <h3 key={i} style={{ fontSize: '18px', fontWeight: 600, color: 'var(--text-h)', marginTop: '28px', marginBottom: '10px' }}>{section.content}</h3>;

    case 'ul':
      return (
        <ul key={i} style={{ paddingLeft: '20px', marginBottom: '20px' }}>
          {section.items.map((item, j) => (
            <li key={j} style={liStyle}>{item}</li>
          ))}
        </ul>
      );

    case 'ol':
      return (
        <ol key={i} style={{ paddingLeft: '20px', marginBottom: '20px' }}>
          {section.items.map((item, j) => (
            <li key={j} style={liStyle}>{item}</li>
          ))}
        </ol>
      );

    case 'code':
      return (
        <pre key={i} style={{
          background: '#1E1E1E', color: '#D4D4D4', borderRadius: '6px',
          padding: '24px', overflowX: 'auto', marginBottom: '24px',
          fontSize: '13px', lineHeight: 1.6, fontFamily: "'Cascadia Code', 'Fira Code', 'Consolas', monospace",
          border: '1px solid #333',
        }}>
          <code>{section.content}</code>
        </pre>
      );

    case 'callout':
      return (
        <div key={i} style={{
          background: 'var(--ms-blue-pale)', border: '1px solid var(--ms-blue)',
          borderLeft: '4px solid var(--ms-blue)', borderRadius: '6px',
          padding: '20px 24px', marginTop: '40px', marginBottom: '8px',
        }}>
          <p style={{ fontSize: '15px', color: 'var(--text-h)', lineHeight: 1.7, margin: 0 }}>
            {section.content}{' '}
            <button
              onClick={() => onNavigate('contact')}
              style={{ color: 'var(--ms-blue)', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', fontSize: '15px', padding: 0, textDecoration: 'underline' }}
            >
              Get in touch →
            </button>
          </p>
        </div>
      );

    default:
      return null;
  }
}

export default function BlogPostPage({ onNavigate }) {
  const { slug } = useParams();
  const nav = useNavigate();
  const post = getPostBySlug(slug);

  if (!post) {
    return (
      <div style={{ maxWidth: '800px', margin: '120px auto', textAlign: 'center', padding: '0 48px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '16px' }}>Article not found</h1>
        <button onClick={() => { nav('/blog'); window.scrollTo(0,0); }} style={{ background: 'var(--ms-blue)', color: '#fff', padding: '12px 28px', border: 'none', borderRadius: '4px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
          Back to Blog
        </button>
      </div>
    );
  }

  const catStyle = CATEGORY_COLORS[post.category] || { color: 'var(--ms-blue)', bg: 'var(--ms-blue-pale)' };
  const related = blogPosts.filter(p => p.slug !== post.slug).slice(0, 3);

  return (
    <div>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={`https://adjustd365.com/blog/${post.slug}`} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BlogPosting',
          'headline': post.title,
          'description': post.metaDescription,
          'datePublished': post.date,
          'author': { '@type': 'Person', 'name': 'AdjustD365', 'url': 'https://adjustd365.com/about' },
          'publisher': { '@type': 'Organization', 'name': 'AdjustD365', 'url': 'https://adjustd365.com' },
          'url': `https://adjustd365.com/blog/${post.slug}`,
          'mainEntityOfPage': `https://adjustd365.com/blog/${post.slug}`,
        })}</script>
        {post.faq && (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': post.faq.map(f => ({
              '@type': 'Question',
              'name': f.q,
              'acceptedAnswer': { '@type': 'Answer', 'text': f.a },
            })),
          })}</script>
        )}
      </Helmet>

      {/* BREADCRUMB */}
      <div className="section-pad" style={{ background: 'var(--bg-2)', borderBottom: '1px solid var(--border)', padding: '20px 48px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', fontSize: '13px', color: 'var(--text-s)', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', color: 'var(--text-s)', cursor: 'pointer', fontSize: '13px' }}>Home</button>
          <span>›</span>
          <button onClick={() => { nav('/blog'); window.scrollTo(0,0); }} style={{ background: 'none', border: 'none', color: 'var(--text-s)', cursor: 'pointer', fontSize: '13px' }}>Blog</button>
          <span>›</span>
          <span style={{ color: 'var(--ms-blue)', fontWeight: 500 }}>{post.category}</span>
        </div>
      </div>

      {/* ARTICLE */}
      <article className="section-pad" style={{ padding: '60px 48px 80px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>

          {/* Meta */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '12px', fontWeight: 600, padding: '3px 10px', borderRadius: '12px', color: catStyle.color, background: catStyle.bg }}>
              {post.category}
            </span>
            <span style={{ fontSize: '13px', color: 'var(--text-s)' }}>{post.date}</span>
            <span style={{ fontSize: '13px', color: 'var(--text-s)' }}>·</span>
            <span style={{ fontSize: '13px', color: 'var(--text-s)' }}>{post.readTime}</span>
          </div>

          {/* Title */}
          <h1 style={{ fontSize: 'clamp(26px,3.5vw,42px)', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text-h)', lineHeight: 1.15, marginBottom: '24px' }}>
            {post.title}
          </h1>

          {/* Excerpt */}
          <p style={{ fontSize: '18px', color: 'var(--text-m)', lineHeight: 1.7, marginBottom: '44px', paddingBottom: '44px', borderBottom: '1px solid var(--border)', fontWeight: 300 }}>
            {post.excerpt}
          </p>

          {/* Body */}
          <div>
            {post.sections.map((section, i) => renderSection(section, i, onNavigate))}
          </div>

          {/* FAQ */}
          {post.faq && (
            <div style={{ marginTop: '48px', paddingTop: '40px', borderTop: '1px solid var(--border)' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '24px', letterSpacing: '-0.01em' }}>
                Frequently Asked Questions
              </h2>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {post.faq.map((f, i) => (
                  <div key={i} style={{ padding: '20px 0', borderBottom: i < post.faq.length - 1 ? '1px solid var(--border)' : 'none' }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '8px' }}>{f.q}</div>
                    <p style={{ fontSize: '14px', color: 'var(--text-m)', lineHeight: 1.75, margin: 0 }}>{f.a}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Author bar */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '56px', paddingTop: '32px', borderTop: '1px solid var(--border)' }}>
            <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'var(--ms-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', fontWeight: 700, color: '#fff', flexShrink: 0 }}>
              F
            </div>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-h)' }}>AdjustD365</div>
              <div style={{ fontSize: '13px', color: 'var(--text-s)' }}>Dynamics 365 Developer & Consultant · 8+ years D365 experience</div>
            </div>
            <button
              onClick={() => onNavigate('about')}
              style={{ marginLeft: 'auto', fontSize: '13px', fontWeight: 600, color: 'var(--ms-blue)', background: 'none', border: '1px solid var(--ms-blue)', borderRadius: '4px', padding: '8px 16px', cursor: 'pointer' }}
            >
              About me
            </button>
          </div>
        </div>
      </article>

      {/* RELATED POSTS */}
      <section className="section-pad" style={{ padding: '60px 48px', background: 'var(--bg-2)', borderTop: '1px solid var(--border)' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-h)', marginBottom: '28px' }}>More D365 Articles</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '20px' }} className="grid-3col">
            {related.map((p) => {
              const cs = CATEGORY_COLORS[p.category] || { color: 'var(--ms-blue)', bg: 'var(--ms-blue-pale)' };
              return (
                <div
                  key={p.slug}
                  onClick={() => { nav(`/blog/${p.slug}`); window.scrollTo(0,0); }}
                  style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '6px', padding: '24px', cursor: 'pointer', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--ms-blue)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
                >
                  <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', color: cs.color, background: cs.bg, display: 'inline-block', marginBottom: '12px' }}>
                    {p.category}
                  </span>
                  <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-h)', lineHeight: 1.4, marginBottom: '8px' }}>{p.title}</div>
                  <div style={{ fontSize: '13px', color: 'var(--ms-blue)', fontWeight: 600, marginTop: '12px' }}>Read →</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <Footer onNavigate={onNavigate} />
    </div>
  );
}
